import React from 'react';
import { Card, Space } from 'antd';
import { HeartOutlined, MedicineBoxOutlined, RiseOutlined, FallOutlined, CheckCircleOutlined } from '@ant-design/icons';
import moment from 'moment';

// Lightweight trends visualization extracted from former modal
const TrendsTab = ({ visits = [] }) => {
  const hasEnough = visits.length > 1;

  const buildBPData = () => visits
    .map(v => ({
      date: moment(v.visitDate),
      systolic: v.vitalSigns?.[v.vitalSigns.length - 1]?.systolicBP,
      diastolic: v.vitalSigns?.[v.vitalSigns.length - 1]?.diastolicBP
    }))
    .filter(d => d.systolic && d.diastolic)
    .sort((a, b) => a.date.unix() - b.date.unix());

  const buildCholData = () => visits
    .map(v => ({
      date: moment(v.visitDate),
      total: v.bloodTests?.[0]?.totalCholesterol,
      hdl: v.bloodTests?.[0]?.hdlCholesterol,
      ldl: v.bloodTests?.[0]?.ldlCholesterol
    }))
    .filter(d => d.total || d.hdl || d.ldl)
    .sort((a, b) => a.date.unix() - b.date.unix());

  const bpData = buildBPData();
  const cholData = buildCholData();

  const renderSingleAwarePolyline = (data, getY, colors) => {
    if (!data.length) return null;
    const chartHeight = 160;
    const chartWidth = 900;
    const padding = 40;
    const single = data.length === 1;
    const getX = (i) => single ? chartWidth / 2 : padding + (i * (chartWidth - 2 * padding)) / (data.length - 1);

    return (
      <svg width="100%" height={chartHeight} style={{ marginTop: 12 }}>
        {[0,1,2,3,4].map(i => (
          <line key={i} x1={padding} y1={i * (chartHeight/4)} x2={chartWidth-padding} y2={i*(chartHeight/4)} stroke="#f0f0f0" />
        ))}
        {!single && colors.map(({ key, stroke }) => (
          <polyline
            key={key}
            points={data.map((d,i)=>`${getX(i)},${getY(d,key,chartHeight)}`).join(' ')}
            fill="none"
            stroke={stroke}
            strokeWidth={3}
          />
        ))}
        {data.map((d,i)=>(
          <g key={i}>
            {colors.map(({ key, stroke }) => (
              d[key] ? <circle key={key} cx={getX(i)} cy={getY(d,key,chartHeight)} r={5} fill={stroke} /> : null
            ))}
          </g>
        ))}
      </svg>
    );
  };

  const insights = (() => {
    const bpSorted = bpData.map(d=>d.systolic).filter(Boolean).sort((a,b)=>a-b);
    const cholSorted = cholData.map(d=>d.total).filter(Boolean).sort((a,b)=>a-b);
    const arr = [];
    if (bpSorted.length >=2) {
      const trend = bpSorted[bpSorted.length-1]-bpSorted[0];
      if (trend > 10) arr.push({ icon:<RiseOutlined style={{color:'#ff4d4f'}} />, text:'Blood pressure trending upward - reinforce lifestyle changes' });
      else if (trend < -10) arr.push({ icon:<FallOutlined style={{color:'#52c41a'}} />, text:'Blood pressure improving - positive trajectory' });
    }
    if (cholSorted.length >=2) {
      const trend = cholSorted[cholSorted.length-1]-cholSorted[0];
      if (trend > 20) arr.push({ icon:<RiseOutlined style={{color:'#ff4d4f'}} />, text:'Cholesterol levels increasing - review diet & exercise' });
      else if (trend < -20) arr.push({ icon:<FallOutlined style={{color:'#52c41a'}} />, text:'Cholesterol decreasing - great progress' });
    }
    if (!arr.length) arr.push({ icon:<CheckCircleOutlined style={{color:'#1890ff'}} />, text:'Values stable - maintain current regimen' });
    return arr;
  })();

  return (
    <Space direction="vertical" size="large" style={{ width:'100%' }}>
      {!hasEnough && (
        <div style={{ textAlign:'center', padding:40, color:'#999', background:'#fafafa', border:'1px dashed #d9d9d9', borderRadius:8 }}>
          Need at least 2 completed visits to display trends.
        </div>
      )}
      {hasEnough && (
        <>
          <Card size="small" title={<span><HeartOutlined style={{color:'#ff4d4f', marginRight:8}}/>Blood Pressure Trend</span>}>
            {renderSingleAwarePolyline(bpData, (d,key,h)=>{
              const maxSys = Math.max(...bpData.map(x=>x.systolic));
              const minDia = Math.min(...bpData.map(x=>x.diastolic));
              const range = maxSys - minDia + 20;
              return ((maxSys + 10 - d[key]) / range) * h;
            }, [
              { key:'systolic', stroke:'#ff4d4f' },
              { key:'diastolic', stroke:'#1890ff' }
            ])}
          </Card>
          <Card size="small" title={<span><MedicineBoxOutlined style={{color:'#722ed1', marginRight:8}}/>Cholesterol Trend</span>}>
            {renderSingleAwarePolyline(cholData, (d,key,h)=>{
              const values = cholData.flatMap(v=>[v.total,v.hdl,v.ldl].filter(Boolean));
              const max = Math.max(...values);
              const min = Math.min(...values);
              const range = max - min + 40;
              return ((max + 20 - d[key]) / range) * h;
            }, [
              { key:'total', stroke:'#722ed1' },
              { key:'hdl', stroke:'#52c41a' },
              { key:'ldl', stroke:'#fa8c16' }
            ])}
          </Card>
          <Card size="small" style={{ background:'#f6ffed', borderColor:'#b7eb8f' }}>
            <Space direction="vertical" size={4}>
              <div style={{ fontWeight:600 }}>Trend Insights</div>
              {insights.map((ins,i)=>(
                <div key={i} style={{ display:'flex', alignItems:'center', gap:8 }}>
                  {ins.icon}
                  <span>{ins.text}</span>
                </div>
              ))}
            </Space>
          </Card>
        </>
      )}
    </Space>
  );
};

export default TrendsTab;
